import DropdownItemSearch from "@app/components/Dropdown/components/dropdowns/items/Search";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Chip } from "@design-system/Inputs/Chip";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { Box } from "@design-system/Layout/Box";
import { FlexRow } from "@design-system/Layout/Flex";
import { type TagType } from "@generated/client/graphql";
import { useState, type FC, type PointerEvent } from "react";
import NewTagDropdownItem from "./NewTagDropdownItem";
import TagDropdownItem from "./TagDropdownItem";
import { useTags } from "./data";

const TagListEditor: FC<{
    values: string[] | undefined;
    onValuesChange: (val: string[]) => void;
    tagType: TagType;
}> = ({ values, onValuesChange, tagType }) => {
    const { tags } = useTags(tagType);

    const [open, setOpen] = useState(false);
    const [searchFilterString, setSearchFilterString] = useState("");

    const selectedTags = (values || []).map((tagId) =>
        tags?.find((tag) => tag.id === tagId),
    );

    const filteredTags = (tags || [])?.filter(
        ({ name }) =>
            !searchFilterString ||
            (name &&
                name.toLowerCase().includes(searchFilterString.toLowerCase())),
    );
    const onToggleTag = (id: string) => {
        if (values?.includes(id)) {
            const newValues = [...values];
            newValues.splice(values.indexOf(id), 1);
            onValuesChange(newValues);
        } else {
            onValuesChange([...(values || []), id]);
        }
    };

    const onSaveNewTagSettled = ({ name }: { name: string }) => {
        const newTag = tags?.find((tag) => tag.name === name);
        if (newTag) {
            onToggleTag(newTag.id);
        }
    };

    const stopEventPropagation = (e: PointerEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <div>
            <Dropdown
                open={open}
                onOpenChange={setOpen}
                closeOnValueChange={false}
                selectType="multi"
            >
                <Dropdown.Trigger asChild>
                    <FlexRow wrap="wrap" className="-m-0.5" alignItems="center">
                        {(selectedTags || []).filter(Boolean).map((tag) => (
                            <Box p="0.5" key={tag?.id}>
                                <Chip size="sm">
                                    <Chip.Value>{tag?.name}</Chip.Value>
                                </Chip>
                            </Box>
                        ))}
                        <Box p="0.5">
                            <Button variant="tonal" size="xs" className="h-6">
                                <Icon name="plus" size="xs" />
                            </Button>
                        </Box>
                    </FlexRow>
                </Dropdown.Trigger>
                <Dropdown.Content
                    align="start"
                    hasPortal
                    onClick={stopEventPropagation}
                >
                    <DropdownItemSearch
                        searchFilterString={searchFilterString}
                        setSearchFilterString={setSearchFilterString}
                    />
                    <NewTagDropdownItem
                        type={tagType}
                        onSaveNewTagSettled={onSaveNewTagSettled}
                    />

                    {filteredTags.map((tag) => (
                        <TagDropdownItem
                            key={tag.id}
                            tagId={tag.id}
                            tagName={tag.name}
                            tagType={tagType}
                            checked={values?.includes(tag.id)}
                            onToggle={onToggleTag}
                        />
                    ))}
                </Dropdown.Content>
            </Dropdown>
        </div>
    );
};

export default TagListEditor;
